import type { NumberFormat } from '@/features/summary-statistics/cardDisplay'
import { apiClient } from '@/core/api'
import { STATS_DEFAULT_ACTIVE_CARDS } from '@/config/demoConfig'

/**
 * The stat cards' server-persisted settings, without a framework (Phase 1H).
 *
 * These settings decide WHICH CARDS ARE SHOWN, and the server owns that: the
 * component reads `activeCards` from this query and nothing else. The Zustand
 * store's copy (`core/state/statsSettings.ts`) is written by the card row's
 * close button and by `core/commands/stats.ts`, and is never read back here.
 *
 * The key, the defaults and the response shapes live here so the Vue composable
 * (`features/map-customization/composables/useSummaryStatsSettings.js`, which
 * eight files use) and the React hook that replaces the cards in 1I address the
 * SAME cache entry. A retyped key is how two caches silently diverge.
 */

export type ActiveCards = Record<string, boolean>

export type SummaryStatsSettings = {
  comparisonMode: boolean
  baselineScenarioId: number
  capacityCalculationMethod: string
  serviceLevelCalculationMethod: string
  presentationMode: boolean
  numberFormat: NumberFormat
  facilityUOM: string | null
  laneUOM: string | null
  customerUOM: string | null
  /** The unit Total Demand is shown in, and whether its label is drawn. */
  demandUOM: string | null
  showDemandUOM: boolean
  activeCards: ActiveCards
  [key: string]: unknown
}

/** A fresh copy every call: callers merge into it, and a shared object would accumulate their edits. */
export function defaultSummaryStatsSettings(): SummaryStatsSettings {
  return {
    comparisonMode: false,
    baselineScenarioId: 1,
    capacityCalculationMethod: 'average',
    serviceLevelCalculationMethod: 'volume_weighted',
    // TRUE, matching core/state/statsSettings.ts. The two disagreed until Phase
    // 1I — this said false, the store said true — and the bar reads the store,
    // so the difference was invisible right up until something read this one.
    presentationMode: true,
    numberFormat: 'abbreviated',
    facilityUOM: null,
    laneUOM: null,
    customerUOM: null,
    // Read by the Total Demand card and written by the Customize Map tab; they
    // survived only through the index signature until Phase 1I, so a TypeScript
    // reader got `unknown`.
    demandUOM: null,
    showDemandUOM: true,
    activeCards: { ...STATS_DEFAULT_ACTIVE_CARDS },
  }
}

/**
 * The scenario id this query is for, or null when there is not one yet.
 * 0, NaN, null and undefined all mean "no scenario", and the query stays off.
 */
export function resolveSettingsScenarioId(raw: unknown): number | null {
  const value = typeof raw === 'object' && raw !== null ? (raw as { value?: unknown }).value : raw
  if (value == null) return null
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return null
  return n
}

export function summaryStatsSettingsKey(scenarioId: number | null, workspaceId: number) {
  return ['summaryStatsSettings', scenarioId, workspaceId] as const
}

/**
 * The server answers in three shapes: the settings object on its own, a
 * `{ success, settings }` envelope, or null when the scenario has no row. All
 * three become a settings object; a missing or non-object `activeCards` is
 * backfilled with the defaults, and nothing else is touched — a saved value of
 * `false` for a card must survive.
 */
export function normalizeSettingsResponse(
  response: unknown,
  defaults: SummaryStatsSettings = defaultSummaryStatsSettings(),
): SummaryStatsSettings {
  if (!response || typeof response !== 'object') return defaults
  const envelope = response as { success?: unknown; settings?: unknown }
  const settings = envelope.success && envelope.settings ? envelope.settings : envelope.success ? null : response
  if (!settings || typeof settings !== 'object') return defaults
  const result = settings as SummaryStatsSettings
  if (!result.activeCards || typeof result.activeCards !== 'object') {
    result.activeCards = { ...defaults.activeCards }
  }
  return result
}

export async function fetchSummaryStatsSettings(
  scenarioId: number | null,
  workspaceId: number,
): Promise<SummaryStatsSettings> {
  const defaults = defaultSummaryStatsSettings()
  if (!scenarioId) return defaults
  try {
    const response = await apiClient.get(`/summary-statistics/settings/${scenarioId}`, {
      params: { workspace_id: workspaceId },
    })
    return normalizeSettingsResponse(response.data, defaults)
  } catch (error) {
    // Settings are a preference, not the data: a failed load must not empty the
    // bar. This is why the query never reports an error to its callers.
    console.warn('[summaryStatsSettings] load failed, using defaults', error)
    return defaults
  }
}

export async function saveSummaryStatsSettings(
  scenarioId: number | null,
  settings: Partial<SummaryStatsSettings>,
  workspaceId: number,
): Promise<SummaryStatsSettings> {
  if (!scenarioId) return settings as SummaryStatsSettings
  const response = await apiClient.post(`/summary-statistics/settings/${scenarioId}`, {
    settings,
    workspace_id: workspaceId,
  })
  const data = response.data as { success?: unknown; settings?: unknown; error?: string } | null
  if (data && typeof data === 'object' && !data.success) return data as unknown as SummaryStatsSettings
  if (data && data.success) return (data.settings || settings) as SummaryStatsSettings
  throw new Error(data?.error || 'Failed to save settings')
}

export async function deleteSummaryStatsSettings(
  scenarioId: number | null,
  workspaceId: number,
): Promise<SummaryStatsSettings> {
  const defaults = defaultSummaryStatsSettings()
  if (!scenarioId) return defaults
  const response = await apiClient.delete(`/summary-statistics/settings/${scenarioId}`, {
    params: { workspace_id: workspaceId },
  })
  const data = response.data as { success?: unknown; error?: string } | null
  if (data?.success) return defaults
  throw new Error(data?.error || 'Failed to delete settings')
}

/**
 * The optimistic write the Customize Map tab depends on: the modal and the bar
 * agree only because this lands in the shared cache before the round trip.
 * `activeCards` MERGES; everything else replaces.
 */
export function mergeSettings(
  current: SummaryStatsSettings | undefined,
  incoming: Partial<SummaryStatsSettings>,
): SummaryStatsSettings {
  const base = current || defaultSummaryStatsSettings()
  const merged = { ...base, ...incoming } as SummaryStatsSettings
  if (incoming.activeCards && typeof incoming.activeCards === 'object') {
    merged.activeCards = { ...base.activeCards, ...incoming.activeCards }
  }
  return merged
}
